
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { getAvatarUrl } from '@/utils/storage';
import { canPractitionerBeListedPublic } from '@/utils/practitioner-mode';

interface FeaturedPractitioner {
  id: number;
  name: string;
  specialization: string;
  avatarUrl: string;
  profileUrl: string;
  rating: number;
  reviewCount: number;
  bio: string;
  services: string[];
}

const fetchFeaturedPractitioners = async (): Promise<FeaturedPractitioner[]> => {
  // Step 1: Get featured practitioner IDs
  const { data: featured, error: featErr } = await supabase
    .from('featured_practitioners')
    .select('practitioner_id, sort_order')
    .eq('is_active', true)
    .order('sort_order', { ascending: true })
    .limit(3);
  if (featErr) throw featErr;

  let baseData: any[] = [];
  if (featured && featured.length > 0) {
    const ids = featured.map(f => f.practitioner_id);
    // Single query with all needed fields + filters
    const { data: practitionersData, error: pracErr } = await supabase
      .from('practitioners')
      .select('id, first_name, last_name, professional_title, bio, avatar_url, average_rating, total_reviews, slug, practice_mode, license_status, subscription_status, availability_rules')
      .in('id', ids)
      .eq('profile_status', 'approved')
      .eq('is_verified', true)
      .eq('accepting_new_clients', true);
    if (pracErr) throw pracErr;

    baseData = (practitionersData || []).sort((a, b) => {
      const aOrder = featured.find(f => f.practitioner_id === a.id)?.sort_order || 999;
      const bOrder = featured.find(f => f.practitioner_id === b.id)?.sort_order || 999;
      return aOrder - bOrder;
    });
  }

  // Fallback
  if (baseData.length === 0) {
    const { data, error } = await supabase
      .rpc('get_public_practitioners')
      .order('total_reviews', { ascending: false })
      .order('average_rating', { ascending: false })
      .order('first_name', { ascending: true })
      .limit(3);
    if (error) throw error;
    baseData = data || [];
  }

  const ids = baseData.map((p: any) => p.id);
  if (ids.length === 0) return [];

  // Parallel: fetch services + listing meta (if not already included)
  const needsMeta = !('practice_mode' in (baseData[0] || {}));
  const [svcResult, metaResult] = await Promise.all([
    supabase.from('services').select('practitioner_id, service_name').in('practitioner_id', ids).eq('is_active', true),
    needsMeta
      ? supabase.from('practitioners').select('id, practice_mode, license_status, subscription_status, availability_rules').in('id', ids)
      : Promise.resolve({ data: null, error: null }),
  ]);
  if (svcResult.error) throw svcResult.error;

  const servicesByPractitioner = new Map<string, string[]>();
  (svcResult.data || []).forEach((s: any) => {
    const arr = servicesByPractitioner.get(s.practitioner_id) || [];
    arr.push(s.service_name);
    servicesByPractitioner.set(s.practitioner_id, arr);
  });

  let listingMetaById = new Map<string, any>();
  if (needsMeta) {
    if (metaResult.error) {
      listingMetaById = new Map(ids.map(id => [id, { practice_mode: 'wellness', license_status: 'not_required', subscription_status: 'inactive', availability_rules: null }]));
    } else {
      listingMetaById = new Map((metaResult.data || []).map((item: any) => [item.id, item]));
    }
  } else {
    listingMetaById = new Map(baseData.map((p: any) => [p.id, p]));
  }

  baseData = baseData.filter((practitioner: any) => {
    const meta = listingMetaById.get(practitioner.id) || {};
    const activeServiceCount = (servicesByPractitioner.get(practitioner.id) || []).length;
    return canPractitionerBeListedPublic(meta, activeServiceCount);
  });

  return baseData.map((practitioner: any, index: number) => {
    let avatarUrl = practitioner.avatar_url || '';
    if (avatarUrl && !(avatarUrl.startsWith('http') || avatarUrl.startsWith('data:'))) {
      avatarUrl = getAvatarUrl('practitioner.photo', avatarUrl);
    }
    return {
      id: index + 1,
      name: `${practitioner.first_name || ''} ${practitioner.last_name || ''}`.trim() || 'Professional',
      specialization: practitioner.professional_title || 'Spiritual Practitioner',
      avatarUrl: avatarUrl || '/placeholder.svg',
      profileUrl: `/book-an-appointment/${practitioner.slug || practitioner.id}`,
      rating: Number(practitioner.average_rating) || 0,
      reviewCount: practitioner.total_reviews || 0,
      bio: practitioner.bio || 'Experienced practitioner helping clients achieve balance and wellness.',
      services: servicesByPractitioner.get(practitioner.id) || []
    };
  });
};

export const useFeaturedPractitioners = () => {
  const { data: practitioners = [], isLoading: loading, error } = useQuery({
    queryKey: ['featured-practitioners'],
    queryFn: fetchFeaturedPractitioners,
    staleTime: 5 * 60 * 1000,
    retry: 2,
  });

  return { practitioners, loading, error: error instanceof Error ? error.message : error ? String(error) : null };
};
